import { BaseService } from '@/shared/services/BaseService';
import { supabase } from '@/shared/services/supabaseClient';
import type { Row } from '@/shared/services/BaseService';

export type Notification = Row<'notifications'>;
export type NotificationPreference = Row<'notification_preferences'>;

class NotificationServiceClass extends BaseService<'notifications'> {
  constructor() {
    super('notifications');
  }

  async getRecent(limit = 20): Promise<Notification[]> {
    const result = await this.query()
      .eq('user_id' as never, this.getUserId() as never)
      .order('created_at' as never, { ascending: false } as never)
      .limit(limit);

    if (result.error) throw result.error;
    return (result.data) as unknown as Notification[];
  }

  async getUnreadCount(): Promise<number> {
    const result = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', this.getOrgId())
      .eq('user_id', this.getUserId())
      .is('read_at', null)
      .is('deleted_at', null);

    if (result.error) throw result.error;
    return result.count ?? 0;
  }

  async markAsRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({
        read_at: new Date().toISOString(),
        updated_by: this.getUserId(),
      })
      .eq('id', id)
      .eq('organization_id', this.getOrgId());

    if (error) throw error;
  }

  async markAllAsRead(): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({
        read_at: new Date().toISOString(),
        updated_by: this.getUserId(),
      })
      .eq('organization_id', this.getOrgId())
      .eq('user_id', this.getUserId())
      .is('read_at', null)
      .is('deleted_at', null);

    if (error) throw error;
  }
}

export const notificationService = new NotificationServiceClass();

class NotificationPreferenceServiceClass extends BaseService<'notification_preferences'> {
  constructor() {
    super('notification_preferences');
  }

  async getPreferences(): Promise<NotificationPreference[]> {
    const result = await this.query()
      .eq('user_id' as never, this.getUserId() as never);

    if (result.error) throw result.error;
    return (result.data) as unknown as NotificationPreference[];
  }

  async updatePreference(
    eventKey: string,
    updates: { channel_email?: boolean; channel_in_app?: boolean },
  ): Promise<void> {
    const existing = await supabase
      .from('notification_preferences')
      .select('id')
      .eq('organization_id', this.getOrgId())
      .eq('user_id', this.getUserId())
      .eq('event_key', eventKey)
      .single();

    if (existing.data) {
      const { error } = await supabase
        .from('notification_preferences')
        .update({
          ...updates,
          updated_by: this.getUserId(),
        })
        .eq('id', existing.data.id);

      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('notification_preferences')
        .insert({
          organization_id: this.getOrgId(),
          user_id: this.getUserId(),
          event_key: eventKey,
          channel_in_app: updates.channel_in_app ?? true,
          channel_email: updates.channel_email ?? true,
          created_by: this.getUserId(),
          updated_by: this.getUserId(),
        });

      if (error) throw error;
    }
  }
}

export const notificationPreferenceService = new NotificationPreferenceServiceClass();
